import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import StarRating from "@/components/StarRating";
import { resolveUrl } from "@/components/ImageUploader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut, ShoppingBag, Heart, User, Star, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function CustomerProfilePage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [profile, setProfile] = useState({});

  useEffect(() => {
    api.get("/customers/me/orders").then((r) => setOrders(r.data));
    api.get("/wishlist/me").then((r) => setWishlist(r.data));
    api.get("/reviews/me").then((r) => setReviews(r.data));
  }, []);

  useEffect(() => {
    if (user) setProfile({
      name: user.name || "", surname: user.surname || "", phone: user.phone || "",
      address: user.address || "", city: user.city || "", cap: user.cap || "", country: user.country || "Italia",
      consenso_marketing: user.consenso_marketing || false,
    });
  }, [user]);

  if (!user) { navigate("/login"); return null; }

  const saveProfile = async () => {
    try {
      await api.put("/customer/me", profile);
      toast.success("Profilo aggiornato");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const removeFromWishlist = async (item) => {
    try {
      await api.delete(`/wishlist/me?sku_variante=${item.sku_variante}&tenant_slug=${item.tenant_slug}`);
      setWishlist((w) => w.filter((x) => x.id !== item.id));
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="customer-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading font-bold text-base">TotalKlick Hub</Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button data-testid="customer-logout" onClick={async () => { await logout(); navigate("/"); }}
              className="inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-sm border border-border hover:bg-accent">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        <div className="mb-8">
          <div className="overline text-brand mb-2 flex items-center gap-2"><User className="w-3 h-3" /> Area riservata</div>
          <h1 className="editorial-h1 text-4xl lg:text-5xl mb-1">Ciao {user.name || user.email}</h1>
          <p className="text-muted-foreground">{user.email}</p>
        </div>

        <Tabs defaultValue="orders">
          <TabsList data-testid="profile-tabs" className="bg-white">
            <TabsTrigger value="orders" data-testid="tab-orders">
              <ShoppingBag className="w-4 h-4 mr-1.5" /> Ordini ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="wishlist" data-testid="tab-wishlist">
              <Heart className="w-4 h-4 mr-1.5" /> Preferiti ({wishlist.length})
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              <Star className="w-4 h-4 mr-1.5" /> Recensioni ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">
              <User className="w-4 h-4 mr-1.5" /> Profilo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-6">
            {orders.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-border rounded-sm text-muted-foreground bg-white">
                Nessun ordine ancora. Esplora le <Link to="/" className="text-brand hover:underline">vetrine</Link>.
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o.id} className="bg-white border border-border rounded-sm p-5 hover:border-brand" data-testid={`my-order-${o.numero_ordine}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-mono text-sm">{o.numero_ordine}</div>
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                        o.status_logistico === "consegnato" ? "bg-emerald-100 text-emerald-700" :
                        o.status_logistico === "spedito" ? "bg-blue-100 text-blue-700" : "bg-accent text-foreground"
                      }`}>{o.status_logistico}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">{o.items?.length} articoli · € {o.totale}</div>
                    {o.tracking_number && (
                      <div className="mt-2 text-xs">
                        Tracking <b>{o.courier_name}</b>: <span className="font-mono">{o.tracking_number}</span>
                        {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-brand ml-2 hover:underline">Traccia →</a>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="wishlist" className="mt-6">
            {wishlist.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-border rounded-sm text-muted-foreground bg-white">
                Nessun prodotto nei preferiti. Clicca sul cuore ♥ nei prodotti.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {wishlist.map((it) => (
                  <div key={it.id} className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`wishlist-item-${it.sku_variante}`}>
                    <Link to={`/s/${it.tenant_slug}/product/${it.sku_variante}`} className="block">
                      <div className="aspect-square bg-neutral-100 overflow-hidden">
                        {it.immagini?.[0] && <img src={resolveUrl(it.immagini[0])} alt="" className="w-full h-full object-cover" />}
                      </div>
                    </Link>
                    <div className="p-3">
                      <div className="text-xs text-muted-foreground mb-1">{it.tenant_name}</div>
                      <div className="font-medium text-sm leading-tight">{it.nome}</div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-heading font-bold">€ {it.prezzo_finale}</span>
                        <button data-testid={`wishlist-remove-${it.sku_variante}`} onClick={() => removeFromWishlist(it)}
                          className="text-red-700 hover:bg-red-50 w-7 h-7 rounded-sm flex items-center justify-center">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            {reviews.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-border rounded-sm text-muted-foreground bg-white">
                Nessuna recensione scritta ancora.
              </div>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="bg-white border border-border rounded-sm p-5" data-testid={`my-review-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <StarRating value={r.rating} />
                      <span className="text-xs text-muted-foreground font-mono">{r.sku_madre}</span>
                    </div>
                    <div className="font-medium">{r.title}</div>
                    <p className="text-sm text-muted-foreground mt-1">{r.body}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <div className="bg-white border border-border rounded-sm p-6 max-w-2xl space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <PInput k="name" label="Nome" profile={profile} setProfile={setProfile} />
                <PInput k="surname" label="Cognome" profile={profile} setProfile={setProfile} />
                <PInput k="phone" label="Telefono" profile={profile} setProfile={setProfile} colSpan={2} />
                <PInput k="address" label="Indirizzo" profile={profile} setProfile={setProfile} colSpan={2} />
                <PInput k="city" label="Città" profile={profile} setProfile={setProfile} />
                <PInput k="cap" label="CAP" profile={profile} setProfile={setProfile} />
                <PInput k="country" label="Paese" profile={profile} setProfile={setProfile} colSpan={2} />
              </div>
              <button data-testid="profile-save" onClick={saveProfile}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover">
                <Save className="w-4 h-4" /> Salva profilo
              </button>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const PInput = ({ k, label, profile, setProfile, colSpan = 1 }) => (
  <div className={colSpan === 2 ? "col-span-2" : ""}>
    <label className="overline text-muted-foreground mb-1 block">{label}</label>
    <input data-testid={`profile-${k}`} value={profile[k] || ""} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })}
      className="w-full h-10 px-3 rounded-sm border border-border text-sm" />
  </div>
);
